import { create } from 'zustand';

/**
 * Code editor preferences (Settings → Code Editor), kept per browser in
 * localStorage and applied live to every Monaco editor.
 */
export interface EditorPrefs {
  fontSize: number;
  tabSize: number;
  /** Insert the closing bracket / quote when typing the opening one. */
  autoClose: boolean;
  /** Indent new lines automatically (off: a new line starts at column 1). */
  autoIndent: boolean;
  formatOnPaste: boolean;
  /** Completion popup while typing (Enter / Tab accept it). */
  suggest: boolean;
  wordWrap: boolean;
  minimap: boolean;
}

export const DEFAULT_EDITOR_PREFS: EditorPrefs = {
  fontSize: 13.5,
  tabSize: 2,
  autoClose: true,
  autoIndent: true,
  formatOnPaste: false,
  suggest: true,
  wordWrap: false,
  minimap: true,
};

const KEY = 'ai-studio.editor-prefs';

function load(): EditorPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULT_EDITOR_PREFS, ...(JSON.parse(raw) as Partial<EditorPrefs>) };
  } catch {
    // Storage blocked or bad JSON: defaults.
  }
  return { ...DEFAULT_EDITOR_PREFS };
}

export const useEditorPrefs = create<EditorPrefs & { set: (patch: Partial<EditorPrefs>) => void; reset: () => void }>((setState, get) => ({
  ...load(),
  set: (patch) => {
    setState(patch);
    const { set: _s, reset: _r, ...prefs } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      // Not persisted; still applied for this session.
    }
  },
  reset: () => get().set({ ...DEFAULT_EDITOR_PREFS }),
}));

/** Monaco options for the preferences. */
export function monacoOptions(p: EditorPrefs) {
  return {
    fontSize: p.fontSize,
    tabSize: p.tabSize,
    autoClosingBrackets: p.autoClose ? ('languageDefined' as const) : ('never' as const),
    autoClosingQuotes: p.autoClose ? ('languageDefined' as const) : ('never' as const),
    autoSurround: p.autoClose ? ('languageDefined' as const) : ('never' as const),
    autoIndent: p.autoIndent ? ('full' as const) : ('none' as const),
    formatOnPaste: p.formatOnPaste,
    formatOnType: false,
    quickSuggestions: p.suggest,
    suggestOnTriggerCharacters: p.suggest,
    acceptSuggestionOnEnter: p.suggest ? ('on' as const) : ('off' as const),
    tabCompletion: 'off' as const,
    parameterHints: { enabled: p.suggest },
    wordWrap: p.wordWrap ? ('on' as const) : ('off' as const),
    minimap: { enabled: p.minimap, maxColumn: 80 },
  };
}
